import React, { useState, useEffect } from 'react';
import { Shield, Key, Smartphone, Check, AlertCircle, Lock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { supabase } from '../lib/supabase';
import { authService } from '../services/authService';
import { ageVerificationService, AgeVerificationStatus } from '../services/ageVerificationService';

const SecurityTab: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const { select, t } = useLanguage();
  
  // Password Change State
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  // 2FA State
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaError, setMfaError] = useState('');
  const [showQrCode, setShowQrCode] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [mfaId, setMfaId] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [isMfaEnabled, setIsMfaEnabled] = useState(false);

  const [ageStatus, setAgeStatus] = useState<AgeVerificationStatus>('unverified');
  const [, setAgeVerified] = useState(false);
  const [ageLoading, setAgeLoading] = useState(false);
  const [ageError, setAgeError] = useState('');
  const [ageSuccess, setAgeSuccess] = useState('');
  const [documentType, setDocumentType] = useState('id_card');
  const [documentCountry, setDocumentCountry] = useState('ES');
  const [documentNumberLast4, setDocumentNumberLast4] = useState('');
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [ageConsent, setAgeConsent] = useState(false);

  useEffect(() => {
    checkMfaStatus();
    loadAgeStatus();
  }, []);

  const loadAgeStatus = async () => {
    if (!user) return;
    try {
      setAgeLoading(true);
      setAgeError('');
      const status = await ageVerificationService.getCurrentStatus();
      setAgeStatus(status.age_verification_status);
      setAgeVerified(status.age_verified);
    } catch {
      setAgeError(t('auth.didit.error.loadStatus', 'No se pudo cargar el estado de verificación de edad.'));
    } finally {
      setAgeLoading(false);
    }
  };

  const checkMfaStatus = async () => {
    try {
      const { error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (error) throw error;
      
      // Check if user has any factors enrolled
      const { data: factors } = await supabase.auth.mfa.listFactors();
      setIsMfaEnabled(factors && factors.all.length > 0 ? true : false);
    } catch (err) {
      console.error('Error checking MFA status:', err);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');
    setPasswordLoading(true);

    if (newPassword !== confirmPassword) {
      setPasswordError(select({ es: 'Las contraseñas no coinciden', en: 'Passwords do not match' }));
      setPasswordLoading(false);
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError(select({ es: 'La contraseña debe tener al menos 8 caracteres', en: 'Password must be at least 8 characters' }));
      setPasswordLoading(false);
      return;
    }

    try {
      if (user?.email) {
        try {
          await authService.signIn(user.email, currentPassword);
        } catch {
          throw new Error(select({ es: 'La contraseña actual es incorrecta', en: 'Current password is incorrect' }));
        }
      }

      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;

      setPasswordSuccess(select({ es: 'Contraseña actualizada exitosamente', en: 'Password updated successfully' }));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : select({ es: 'Error al actualizar contraseña', en: 'Error updating password' }));
    } finally {
      setPasswordLoading(false);
    }
  };

  const startMfaEnrollment = async () => {
    setMfaLoading(true);
    setMfaError('');
    try {
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp'
      });
      if (error) throw error;

      setMfaId(data.id);
      setQrCodeUrl(data.totp.qr_code);
      setShowQrCode(true);
    } catch (err) {
      setMfaError(err instanceof Error ? err.message : 'Error starting MFA enrollment');
    } finally {
      setMfaLoading(false);
    }
  };

  const verifyMfaEnrollment = async () => {
    setMfaLoading(true);
    setMfaError('');
    try {
      const { error } = await supabase.auth.mfa.challengeAndVerify({
        factorId: mfaId,
        code: verifyCode
      });
      if (error) throw error;

      setShowQrCode(false);
      setIsMfaEnabled(true);
      setVerifyCode('');
      await refreshUser();
    } catch {
      setMfaError(select({ es: 'Código inválido. Inténtalo de nuevo.', en: 'Invalid code. Try again.' }));
    } finally {
      setMfaLoading(false);
    }
  };

  const disableMfa = async () => {
    if (!window.confirm(select({ es: '¿Estás seguro de que quieres desactivar la autenticación en dos pasos?', en: 'Are you sure you want to disable 2FA?' }))) {
      return;
    }

    setMfaLoading(true);
    try {
      const { data: factors } = await supabase.auth.mfa.listFactors();
      if (factors?.all) {
        for (const factor of factors.all) {
          await supabase.auth.mfa.unenroll({ factorId: factor.id });
        }
      }
      setIsMfaEnabled(false);
    } catch {
      setMfaError(select({ es: 'Error al desactivar 2FA', en: 'Error disabling 2FA' }));
    } finally {
      setMfaLoading(false);
    }
  };

  const handleAgeVerification = async () => {
    if (!documentFile || !ageConsent || !user?.birth_date) return;

    setAgeLoading(true);
    setAgeError('');
    setAgeSuccess('');

    try {
      await ageVerificationService.startVerification({
        documentType,
        documentCountry,
        documentNumberLast4,
        dateOfBirth: user.birth_date,
        file: documentFile,
      });
      setAgeSuccess(t('auth.didit.success.documentSent', 'Documento enviado. Revisaremos tu verificación.'));
      setAgeStatus('pending');
    } catch {
      setAgeError(t('auth.didit.error.startVerification', 'Error al iniciar verificación. Inténtalo de nuevo.'));
    } finally {
      setAgeLoading(false);
    }
  };

  const handleDiditVerification = async () => {
    if (!ageConsent) {
      setAgeError(t('auth.didit.error.consentRequired', 'Debes aceptar el consentimiento para continuar.'));
      return;
    }
    
    setAgeLoading(true);
    setAgeError('');
    
    try {
      // URL de tu servidor backend privado
      // En desarrollo local, asegúrate de que este puerto coincida con tu servidor Node.js
      const BACKEND_URL = 'http://localhost:3000'; 
      
      const { url } = await ageVerificationService.startDiditVerification(BACKEND_URL);
      
      // Redirigir al usuario a Didit
      window.location.href = url;
    } catch (err) {
      console.error(err);
      setAgeError(t('auth.didit.error.connection', 'Error al conectar con el servidor de verificación. Asegúrate de que el backend está corriendo.'));
      setAgeLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Password Change Section */}
      <section className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <Key className="text-purple-600" size={24} />
          {select({ es: 'Cambiar contraseña', en: 'Change password' })}
        </h3>

        {passwordError && (
          <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg flex items-center gap-3">
            <AlertCircle className="text-red-500" size={20} />
            <p className="text-red-700">{passwordError}</p>
          </div>
        )}

        {passwordSuccess && (
          <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-500 rounded-lg flex items-center gap-3">
            <Check className="text-green-500" size={20} />
            <p className="text-green-700">{passwordSuccess}</p>
          </div>
        )}

        <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {select({ es: 'Contraseña actual', en: 'Current password' })}
            </label>
            <input
              type="password"
              required
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {select({ es: 'Nueva contraseña', en: 'New password' })}
            </label>
            <input
              type="password"
              required
              minLength={8}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              {select({ es: 'Mínimo 8 caracteres, debe incluir números y símbolos', en: 'Minimum 8 characters, must include numbers and symbols' })}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {select({ es: 'Confirmar nueva contraseña', en: 'Confirm new password' })}
            </label>
            <input
              type="password"
              required
              minLength={8}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          <button
            type="submit"
            disabled={passwordLoading}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
          >
            {passwordLoading ? select({ es: 'Actualizando...', en: 'Updating...' }) : select({ es: 'Actualizar contraseña', en: 'Update password' })}
          </button>
        </form>
      </section>

      <section className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <Smartphone className="text-purple-600" size={24} />
          {select({ es: 'Autenticación en dos pasos (2FA)', en: 'Two-Factor Authentication (2FA)' })}
        </h3>

        <div className="flex items-start justify-between">
          <div className="max-w-xl">
            <p className="text-gray-600 mb-4">
              {select({
                es: 'Añade una capa extra de seguridad a tu cuenta requiriendo un código de tu aplicación de autenticación al iniciar sesión.',
                en: 'Add an extra layer of security to your account by requiring a code from your authenticator app when logging in.'
              })}
            </p>
            {isMfaEnabled ? (
              <div className="flex items-center gap-2 text-green-600 font-medium mb-4">
                <Shield size={20} />
                {select({ es: '2FA está activado', en: '2FA is enabled' })}
              </div>
            ) : (
              <div className="flex items-center gap-2 text-gray-500 font-medium mb-4">
                <Shield size={20} />
                {select({ es: '2FA está desactivado', en: '2FA is disabled' })}
              </div>
            )}
          </div>
          
          {!showQrCode && (
            <button
              onClick={isMfaEnabled ? disableMfa : startMfaEnrollment}
              disabled={mfaLoading}
              className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                isMfaEnabled
                  ? 'bg-red-100 text-red-600 hover:bg-red-200'
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
            >
              {isMfaEnabled
                ? select({ es: 'Desactivar 2FA', en: 'Disable 2FA' })
                : select({ es: 'Activar 2FA', en: 'Enable 2FA' })
              }
            </button>
          )}
        </div>

        {mfaError && (
          <div className="mt-4 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg flex items-center gap-3">
            <AlertCircle className="text-red-500" size={20} />
            <p className="text-red-700">{mfaError}</p>
          </div>
        )}

        {showQrCode && (
          <div className="mt-6 border-t border-gray-200 pt-6">
            <h4 className="font-semibold text-gray-900 mb-4">
              {select({ es: 'Configurar aplicación de autenticación', en: 'Set up authenticator app' })}
            </h4>
            <div className="flex flex-col md:flex-row gap-8 items-center">
              <div className="bg-white p-4 border border-gray-200 rounded-lg">
                <img src={qrCodeUrl} alt="QR Code" className="w-48 h-48" />
              </div>
              <div className="space-y-4 flex-1">
                <ol className="list-decimal list-inside space-y-2 text-gray-600">
                  <li>{select({ es: 'Descarga una app de autenticación (Google Authenticator, Authy, etc.)', en: 'Download an authenticator app (Google Authenticator, Authy, etc.)' })}</li>
                  <li>{select({ es: 'Escanea el código QR', en: 'Scan the QR code' })}</li>
                  <li>{select({ es: 'Introduce el código de 6 dígitos que aparece en la app', en: 'Enter the 6-digit code shown in the app' })}</li>
                </ol>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="w-32 px-4 py-2 border border-gray-300 rounded-lg text-center tracking-widest font-mono text-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <button
                    onClick={verifyMfaEnrollment}
                    disabled={verifyCode.length !== 6 || mfaLoading}
                    className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
                  >
                    {select({ es: 'Verificar', en: 'Verify' })}
                  </button>
                </div>
                <button
                  onClick={() => setShowQrCode(false)}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  {select({ es: 'Cancelar', en: 'Cancel' })}
                </button>
              </div>
            </div>
          </div>
        )}
      </section>

      <section className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <Lock className="text-purple-600" size={24} />
          {t('auth.didit.title', 'Verificación de mayoría de edad')}
        </h3>

        <div className="mb-4">
          <p className="text-gray-600">
            {t('auth.didit.securityTab.description', 'Para cumplir con la normativa y proteger a los usuarios, verificamos que todas las cuentas pertenecen a personas mayores de 18 años mediante documentos oficiales.')}
          </p>
        </div>

        <div className="mb-4">
          <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
            {ageStatus === 'verified' && t('auth.didit.statusLabel.verified', 'Estado: verificado')}
            {ageStatus === 'pending' && t('auth.didit.statusLabel.in_review', 'Estado: en revisión')}
            {ageStatus === 'rejected' && t('auth.didit.statusLabel.rejected', 'Estado: rechazado')}
            {ageStatus === 'unverified' && t('auth.didit.statusLabel.unverified', 'Estado: no verificado')}
          </div>
        </div>

        {ageError && (
          <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg flex items-center gap-3">
            <AlertCircle className="text-red-500" size={20} />
            <p className="text-red-700">{ageError}</p>
          </div>
        )}

        {ageSuccess && (
          <div className="mb-4 p-4 bg-green-50 border-l-4 border-green-500 rounded-lg flex items-center gap-3">
            <Check className="text-green-500" size={20} />
            <p className="text-green-700">{ageSuccess}</p>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-8">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('auth.didit.form.documentType', 'Tipo de documento')}
              </label>
              <select
                value={documentType}
                onChange={(e) => setDocumentType(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="id_card">{t('auth.didit.form.nationalId', 'Documento nacional de identidad')}</option>
                <option value="passport">{t('auth.didit.form.passport', 'Pasaporte')}</option>
                <option value="driver_license">{t('auth.didit.form.driverLicense', 'Permiso de conducir')}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('auth.didit.form.issuingCountry', 'País emisor')}
              </label>
              <input
                type="text"
                value={documentCountry}
                onChange={(e) => setDocumentCountry(e.target.value.toUpperCase())}
                maxLength={2}
                className="w-24 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent uppercase"
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('auth.didit.form.issuingCountryHint', 'Introduce el código ISO de dos letras (ej. ES, FR, DE).')}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('auth.didit.form.last4Digits', 'Últimos 4 dígitos del documento')}
              </label>
              <input
                type="text"
                value={documentNumberLast4}
                onChange={(e) => setDocumentNumberLast4(e.target.value.replace(/\s/g, '').slice(0, 4))}
                className="w-32 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('auth.didit.form.traceabilityHint', 'Usamos esta información para trazabilidad y controles antifraude.')}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('auth.didit.form.birthDate', 'Fecha de nacimiento')}
              </label>
              <input
                type="date"
                value={user?.birth_date || ''}
                readOnly
                disabled
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600"
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('auth.didit.form.birthDateHint', 'La fecha de nacimiento se valida también contra el documento.')}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('auth.didit.form.uploadLabel', 'Sube la imagen de tu documento')}
              </label>
              <input
                type="file"
                accept="image/*,application/pdf"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  setDocumentFile(file);
                }}
                className="w-full text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('auth.didit.form.uploadHint', 'Asegúrate de que los datos sean legibles. Se almacenan de forma segura.')}
              </p>
            </div>

            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                id="ageConsent"
                checked={ageConsent}
                onChange={(e) => setAgeConsent(e.target.checked)}
                className="mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
              />
              <label htmlFor="ageConsent" className="text-sm text-gray-600">
                {t('auth.didit.form.consent', 'Declaro que la información proporcionada es verídica y autorizo el tratamiento de mis datos para la verificación de mi mayoría de edad según la normativa vigente.')}
              </label>
            </div>

            <div className="flex flex-col gap-3">
              <button
                onClick={handleAgeVerification}
                disabled={!documentFile || !ageConsent || ageStatus === 'pending' || ageLoading}
                className="w-full px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {ageLoading 
                  ? t('auth.didit.button.processing', 'Procesando...')
                  : t('auth.didit.button.manualUpload', 'Subir documento manualmente')
                }
              </button>

              <div className="relative flex items-center py-2">
                <div className="flex-grow border-t border-gray-300"></div>
                <span className="flex-shrink-0 mx-4 text-gray-400 text-sm">
                  {t('auth.didit.separator.quickVerification', 'O usa verificación rápida')}
                </span>
                <div className="flex-grow border-t border-gray-300"></div>
              </div>

              <button
                onClick={handleDiditVerification}
                disabled={!ageConsent || ageStatus === 'pending' || ageLoading}
                className="w-full px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <Shield size={18} />
                {ageLoading 
                  ? t('auth.didit.button.connecting', 'Conectando...')
                  : t('auth.didit.button.autoVerify', 'Verificar automáticamente con Didit')
                }
              </button>
            </div>
          </div>
        </div>

        <div className="mt-6 p-4 bg-gray-50 rounded-lg text-sm text-gray-600">
          <p className="font-medium mb-1">
            {t('auth.didit.info.title', 'Sobre el proceso de verificación:')}
          </p>
          <ul className="list-disc list-inside space-y-1">
            <li>
              {t('auth.didit.info.point1', 'En algunos países la verificación se hará automáticamente mediante proveedores especializados o fuentes oficiales.')}
            </li>
            <li>
              {t('auth.didit.info.point2', 'En otros casos, nuestro equipo revisará la documentación manualmente.')}
            </li>
          </ul>
        </div>
      </section>
    </div>
  );
};

export default SecurityTab;
